/**
 * PKCE (RFC 7636) with S256, over the Web Crypto API. No dependency, no Angular.
 *
 * The verifier lives in `sessionStorage`, never `localStorage` — a stated GPGT-41350 rule. The
 * distinction is not stylistic: `localStorage` outlives the tab and is shared across every tab on
 * this origin, so an abandoned sign-in would leave a usable verifier behind for the next person at
 * the machine, and two concurrent sign-ins would overwrite each other's.
 */

const VERIFIER_KEY = 'gz.pkce.verifier';
const STATE_KEY = 'gz.oauth.state';

/** RFC 7636 §4.1 allows 43–128 characters. 96 random bytes → 128 base64url characters. */
const VERIFIER_BYTES = 96;

export interface PkceChallenge {
  readonly verifier: string;
  readonly challenge: string;
  readonly method: 'S256';
}

/** base64url per RFC 4648 §5: no padding, `-` and `_` for `+` and `/`. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * A fresh verifier and its S256 challenge.
 *
 * `plain` is not offered. RFC 7636 permits it and the service rejects it; a function that cannot
 * produce it cannot be talked into producing it.
 */
export async function createPkceChallenge(): Promise<PkceChallenge> {
  const verifier = randomBase64Url(VERIFIER_BYTES);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64UrlEncode(new Uint8Array(digest)), method: 'S256' };
}

/** 32 bytes of CSRF state, to be compared on return. */
export function createState(): string {
  return randomBase64Url(32);
}

/**
 * sessionStorage access, wrapped.
 *
 * Private-mode Safari and "block site data" make `sessionStorage` throw on ACCESS, not just return
 * null. An unguarded read here would break sign-in for those users with a blank screen, so every
 * call site treats storage as best-effort and a missing verifier as a flow that must restart.
 */
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function clearSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* nothing to do: the value we wanted gone is already unreachable */
  }
}

export function rememberVerifier(verifier: string): boolean {
  return writeSession(VERIFIER_KEY, verifier);
}

export function rememberState(state: string): boolean {
  return writeSession(STATE_KEY, state);
}

/** Reads AND clears: a verifier is single-use, so leaving it readable serves no one. */
export function takeVerifier(): string | null {
  const verifier = readSession(VERIFIER_KEY);
  clearSession(VERIFIER_KEY);
  return verifier;
}

export function takeState(): string | null {
  const state = readSession(STATE_KEY);
  clearSession(STATE_KEY);
  return state;
}

/**
 * Constant-time-ish string comparison for the `state` check.
 *
 * `===` on strings short-circuits at the first differing character. The leak is small and the
 * attacker's position is awkward, but `state` is a security parameter and the service's own rule is
 * that secrets are never compared with `===`. This costs nothing and removes the argument.
 */
export function statesMatch(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false;
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    // `charCodeAt` past the end is NaN, but the length check above rules that out.
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/** Called when a flow is abandoned or completed, so nothing reusable is left in the tab. */
export function forgetPkce(): void {
  clearSession(VERIFIER_KEY);
  clearSession(STATE_KEY);
}
